import { binaryReports, failedSteps, GUEST_PROJECT } from "idfweb-build";
import type { PageToRuntime, ProjectReply, RuntimeToPage } from "idfweb-runtime-protocol";
import type { FsClient } from "idfweb-runtime-protocol/fs-rpc";
import { tools, type ToolContext, type ToolRegistry, type ToolResult } from "idfweb-tools";
import { Errno, Filetype, WasiError } from "idfweb-vfs";

import type { DevicePane } from "./device.ts";
import { parseMonitorScript, runMonitorScript } from "./monitor-script.ts";
import { storedPreset } from "./project-state.ts";
import { replaceText, sliceLines } from "./text-edit.ts";

/** How much of the failed steps, or of the end of the output, a build result quotes. */
const FAILURES_KEPT = 16 * 1024;
const OUTPUT_TAIL = 4 * 1024;

/** How often a running build reports its latest output line as progress. */
const PROGRESS_INTERVAL_MS = 2000;

/** How much of each of a command's stdout and stderr a result quotes. */
const COMMAND_OUTPUT_KEPT = 32 * 1024;
const COMMAND_TIMEOUT_S = 60;
const COMMAND_TIMEOUT_MAX_S = 600;

/**
 * How much of a monitor script's output a result quotes, from its start and
 * its end, and how much of the unread output from before it.
 */
const MONITOR_OUTPUT_KEPT = 64 * 1024;
const MONITOR_EARLIER_KEPT = 8 * 1024;

export interface BuildOutcome {
    ok: boolean;
    elf: string | undefined;
    message: string | undefined;
    output: string;
    target: string | undefined;
}

/** Builds the page asks the runtime worker for. */
export class PageBuilds {
    readonly #post: (message: PageToRuntime) => void;
    readonly #running = new Map<
        number,
        {
            output: string[];
            onOutput: (text: string) => void;
            resolve: (outcome: BuildOutcome) => void;
        }
    >();
    #nextId = 1;

    constructor(post: (message: PageToRuntime) => void) {
        this.#post = post;
    }

    build(reconfigure: boolean, heading: string, onOutput: (text: string) => void): Promise<BuildOutcome> {
        const id = this.#nextId++;
        return new Promise((resolve) => {
            this.#running.set(id, { output: [], onOutput, resolve });
            const jobs = Math.max(1, (navigator.hardwareConcurrency || 4) - 1);
            this.#post({ type: "build", id, jobs, reconfigure, heading });
        });
    }

    /** True if the message was about one of these builds. */
    receive(message: RuntimeToPage): boolean {
        if (message.type !== "output" && message.type !== "built") return false;
        const running = this.#running.get(message.id);
        if (running === undefined) return true;
        if (message.type === "output") {
            running.output.push(message.text);
            running.onOutput(message.text);
            return true;
        }
        this.#running.delete(message.id);
        running.resolve({
            ok: message.ok,
            elf: message.elf,
            target: message.target,
            message: message.message,
            output: running.output.join(""),
        });
        return true;
    }
}

/** Questions to the runtime worker about the project root, for the page's tools. */
export class PageProjects {
    readonly #post: (message: PageToRuntime) => void;
    readonly #waiting = new Map<number, (reply: ProjectReply) => void>();
    #nextId = 1;

    constructor(post: (message: PageToRuntime) => void) {
        this.#post = post;
    }

    async list(): Promise<Omit<Extract<ProjectReply, { type: "projects" }>, "type" | "id">> {
        const reply = await this.#ask({ type: "projects", id: this.#nextId++ });
        return reply.type === "projects" ? reply : { root: "", projects: [], target: "" };
    }

    /**
     * Make `path` the project root, with `preset` if given; resolves to its
     * build directory, or to why it could not be.
     */
    async select(path: string, preset?: string): Promise<{ error?: string; buildDir?: string; target?: string }> {
        const id = this.#nextId++;
        const remembered = storedPreset(path.replace(/\/+$/, ""));
        const request = {
            type: "select-project" as const,
            id,
            path,
            ...(preset === undefined ? {} : { preset }),
            ...(remembered === undefined ? {} : { remembered }),
        };
        const reply = await this.#ask(request);
        return reply.type === "select-project" ? reply : {};
    }

    /** Change the chip target as the page's dropdown does; resolves to why it could not be. */
    async setTarget(target: string): Promise<{ error?: string; changed?: boolean; buildDir?: string }> {
        const reply = await this.#ask({ type: "set-target", id: this.#nextId++, target });
        return reply.type === "set-target" ? reply : {};
    }

    receive(message: ProjectReply): void {
        this.#waiting.get(message.id)?.(message);
        this.#waiting.delete(message.id);
    }

    #ask(message: PageToRuntime & { id: number }): Promise<ProjectReply> {
        return new Promise((resolve) => {
            this.#waiting.set(message.id, resolve);
            this.#post(message);
        });
    }
}

export type CommandOutcome = Omit<Extract<RuntimeToPage, { type: "ran" }>, "type" | "id">;

/** Shell commands the page asks the runtime worker to run. */
export class PageCommands {
    readonly #post: (message: PageToRuntime) => void;
    readonly #running = new Map<number, (outcome: CommandOutcome) => void>();
    #nextId = 1;

    constructor(post: (message: PageToRuntime) => void) {
        this.#post = post;
    }

    run(command: string, cwd: string | undefined, timeoutMs: number): Promise<CommandOutcome> {
        const id = this.#nextId++;
        return new Promise((resolve) => {
            this.#running.set(id, resolve);
            this.#post({ type: "command", id, command, cwd, timeoutMs });
        });
    }

    /** True if the message was about one of these commands. */
    receive(message: RuntimeToPage): boolean {
        if (message.type !== "ran") return false;
        const { type: _type, id, ...outcome } = message;
        this.#running.get(id)?.(outcome);
        this.#running.delete(id);
        return true;
    }
}

export function addPageTools(
    registry: ToolRegistry,
    deps: {
        builds: PageBuilds;
        projects: PageProjects;
        commands: PageCommands;
        fs: FsClient;
        device: DevicePane;
    },
): void {
    const { builds, projects, commands, fs, device } = deps;

    registry.add(tools.build, async ({ reconfigure }, context) => {
        const outcome = await builds.build(
            reconfigure ?? false,
            reconfigure ? "configure and build, for an agent" : "build, for an agent",
            reportLatestLine(context),
        );
        const chip = outcome.target === undefined ? "" : ` for ${outcome.target}`;
        const summary = outcome.ok
            ? `The build${chip} succeeded: ${outcome.elf}`
            : `The build${chip} failed: ${outcome.message ?? "see the output"}`;
        const failures = failedSteps(outcome.output)
            .map((step) => `FAILED: ${step.target}\n${step.output}`)
            .join("\n\n");
        const parts = [summary];
        if (failures !== "") parts.push(`Failed steps:\n${head(failures, FAILURES_KEPT)}`);
        // A failure outside a ninja step, such as in CMake's configure, is only in the output.
        else if (!outcome.ok) parts.push(`End of the output:\n${tail(outcome.output, OUTPUT_TAIL)}`);
        // The bootloader sub-build is one ninja step, printed when it ends, which is usually
        // after the app's link, so the end of the output rarely holds the app's size. After a
        // failure the build tree can hold an older app, so no binary is listed.
        const reports = outcome.ok ? binaryReports(outcome.output) : [];
        if (reports.length > 0) parts.push(`Binaries:\n${reports.join("\n")}`);
        return {
            text: parts.join("\n\n"),
            isError: !outcome.ok,
        };
    });

    registry.add(tools.readFile, async ({ path, offset, limit }) => {
        if ((offset !== undefined && offset < 1) || (limit !== undefined && limit < 1)) {
            return { text: "offset and limit start at 1.", isError: true };
        }
        const text = await readText(fs, path);
        if (offset === undefined && limit === undefined) return { text };
        const range = sliceLines(text, offset ?? 1, limit);
        if ("error" in range) return { text: `${path}: ${range.error}`, isError: true };
        const { first, last, total } = range;
        const lines = last < first ? "No lines" : `Lines ${first}-${last}`;
        return { text: `${lines} of ${total} of ${path}:\n${range.text}` };
    });

    registry.add(tools.writeFile, async ({ path, content }) => {
        const parts = projectPath(path);
        for (let i = 1; i < parts.length; i++) {
            const dir = `${GUEST_PROJECT}/${parts.slice(0, i).join("/")}`;
            if (!(await exists(fs, dir))) await fsCall(dir, () => fs.mkdir(dir));
        }
        await fsCall(path, () =>
            fs.writeFile(path, new TextEncoder().encode(content), {
                create: true,
                overwrite: true,
            }),
        );
        return { text: `Wrote ${path}.` };
    });

    // An agent may send several edits of a file at once; each reads what the one before wrote.
    let edits: Promise<unknown> = Promise.resolve();
    registry.add(tools.editFile, ({ path, old_string, new_string, replace_all }) => {
        const done = edits.then(() => editFile(path, old_string, new_string, !!replace_all));
        edits = done.catch(() => undefined);
        return done;
    });

    async function editFile(
        path: string,
        oldString: string,
        newString: string,
        replaceAll: boolean,
    ): Promise<ToolResult> {
        projectPath(path);
        const edit = replaceText(await readText(fs, path), oldString, newString, replaceAll);
        if (!edit.ok) return { text: `${path}: ${edit.error}`, isError: true };
        const bytes = new TextEncoder().encode(edit.text);
        await fsCall(path, () => fs.writeFile(path, bytes, { create: false, overwrite: true }));
        const where = edit.lines.map(([from, to]) => (from === to ? `${from}` : `${from}-${to}`));
        const what = edit.count === 1 ? "1 occurrence" : `${edit.count} occurrences`;
        const lines = where.length === 1 && !where[0]!.includes("-") ? "line" : "lines";
        return {
            text: `Replaced ${what} in ${path}; the new text is on ${lines} ${where.join(", ")}.`,
        };
    }

    registry.add(tools.listDirectory, async ({ path }) => {
        const entries = await fsCall(path, () => fs.readdir(path));
        const names = entries
            .map((entry) => (entry.filetype === Filetype.DIRECTORY ? `${entry.name}/` : entry.name))
            .sort();
        return { text: names.length === 0 ? `${path} is empty.` : names.join("\n") };
    });

    registry.add(tools.flash, async () => {
        await device.flash();
        return { text: "The board is flashed and was reset. Use read_serial to see its output." };
    });

    registry.add(tools.readSerial, async () => {
        const text = device.takeSerialOutput().replace(ANSI_ESCAPE, "");
        return {
            text: text === "" ? "No serial output since the previous read_serial call." : text,
        };
    });

    registry.add(tools.monitor, async ({ script, timeout_seconds }, context) => {
        const seconds = Math.min(Math.max(timeout_seconds ?? COMMAND_TIMEOUT_S, 1), COMMAND_TIMEOUT_MAX_S);
        const lines = parseMonitorScript(script);
        if (lines.length === 0) return { text: "The script has no commands.", isError: true };
        const outcome = await runMonitorScript(lines, device, seconds * 1000, (line) => context.progress(line));
        const parts = [outcome.summary];
        if (outcome.earlier !== "") {
            parts.push(`Unread output from before the script:\n${tail(outcome.earlier, MONITOR_EARLIER_KEPT)}`);
        }
        parts.push(`Output of the script:\n${ends(outcome.transcript, MONITOR_OUTPUT_KEPT)}`);
        return { text: parts.join("\n\n"), isError: !outcome.ok };
    });

    registry.add(tools.listProjects, async () => {
        const { root, projects: found, target, presets, preset } = await projects.list();
        const selected = `selected, target ${target}`;
        const lines = found.map((path) => (path === root ? `${path} (${selected})` : path));
        if (!found.includes(root)) lines.unshift(`${root} (${selected}, not an ESP-IDF project)`);
        if (presets !== undefined) {
            const named = presets.map((name) => (name === preset ? `${name} (selected)` : name));
            lines.push("", `CMake presets of ${root}: ${named.join(", ")}`);
        }
        return { text: lines.join("\n") };
    });

    registry.add(tools.selectProject, async ({ path, preset }) => {
        const { error, buildDir, target } = await projects.select(path, preset);
        if (error !== undefined) {
            return { text: `${error}. list_projects lists the projects.`, isError: true };
        }
        return {
            text: `${path} is the project root. Builds configure it into ${buildDir}, ` + `for the target ${target}.`,
        };
    });

    registry.add(tools.setTarget, async ({ target }) => {
        const { error, changed, buildDir } = await projects.setTarget(target);
        if (error !== undefined) return { text: `${error}.`, isError: true };
        if (!changed) return { text: `The target is ${target} already. Nothing was changed.` };
        device.selectTarget(target);
        return {
            text:
                `The target is now ${target}. ${buildDir} was emptied, and the next build moves ` +
                "sdkconfig, if there is one, to sdkconfig.old and configures a new one for the " +
                "target.",
        };
    });

    registry.add(tools.runCommand, async ({ command, cwd, timeout_seconds }) => {
        const seconds = Math.min(Math.max(timeout_seconds ?? COMMAND_TIMEOUT_S, 1), COMMAND_TIMEOUT_MAX_S);
        const outcome = await commands.run(command, cwd, seconds * 1000);
        if (outcome.error !== undefined) {
            return { text: `The command could not be run: ${outcome.error}`, isError: true };
        }
        const status = outcome.timedOut
            ? `The command was stopped after ${seconds} s.`
            : `Exit status ${outcome.exitCode}.`;
        const parts = [status];
        const streams = [
            ["stdout", outcome.stdout],
            ["stderr", outcome.stderr],
        ] as const;
        for (const [name, text] of streams) {
            if (text !== "") parts.push(`${name}:\n${head(text, COMMAND_OUTPUT_KEPT)}`);
        }
        return { text: parts.join("\n\n"), isError: outcome.timedOut || outcome.exitCode !== 0 };
    });
}

const ANSI_ESCAPE = /\x1b\[[0-9;]*[A-Za-z]/g;

/** A progress callback for build output that reports the newest line every few seconds. */
function reportLatestLine(context: ToolContext): (text: string) => void {
    let last = 0;
    return (text) => {
        const now = Date.now();
        if (now - last < PROGRESS_INTERVAL_MS) return;
        const line = text.trimEnd().split("\n").at(-1);
        if (line === undefined || line === "") return;
        last = now;
        context.progress(line);
    };
}

function head(text: string, length: number): string {
    return text.length <= length ? text : `${text.slice(0, length)}…`;
}

function tail(text: string, length: number): string {
    return text.length <= length ? text : `…${text.slice(-length)}`;
}

/** The start and the end of `text`, `length` characters in all. */
function ends(text: string, length: number): string {
    if (text.length <= length) return text;
    const half = length / 2;
    const left = text.length - length;
    return `${text.slice(0, half)}\n… ${left} characters left out …\n${text.slice(-half)}`;
}

/** The segments of a path under the project, or an error for any other path. */
function projectPath(path: string): string[] {
    const parts = path.startsWith(`${GUEST_PROJECT}/`) ? path.slice(GUEST_PROJECT.length + 1).split("/") : [];
    if (parts.length === 0 || parts.some((part) => part === "" || part === "." || part === "..")) {
        throw new Error(`${path} is not a file path under ${GUEST_PROJECT}.`);
    }
    return parts;
}

/** The content of a text file, or an error for a binary one. */
async function readText(fs: FsClient, path: string): Promise<string> {
    const bytes = await fsCall(path, () => fs.readFile(path));
    if (bytes.includes(0)) throw new Error(`${path} is a binary file of ${bytes.length} bytes.`);
    return new TextDecoder().decode(bytes);
}

async function exists(fs: FsClient, path: string): Promise<boolean> {
    try {
        await fs.stat(path);
        return true;
    } catch (e) {
        if (e instanceof WasiError && e.errno === Errno.NOENT) return false;
        throw e;
    }
}

/** A filesystem call, with the guest's errno turned into a sentence about `path`. */
async function fsCall<T>(path: string, call: () => Promise<T>): Promise<T> {
    try {
        return await call();
    } catch (e) {
        if (!(e instanceof WasiError)) throw e;
        const why: Partial<Record<number, string>> = {
            [Errno.NOENT]: "does not exist",
            [Errno.ISDIR]: "is a directory",
            [Errno.NOTDIR]: "has a parent that is not a directory",
            [Errno.EXIST]: "already exists",
        };
        throw new Error(`${path} ${why[e.errno] ?? `cannot be accessed (errno ${e.errno})`}.`);
    }
}
